// @ts-nocheck
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { Avatar, Box, ThemeProvider, Typography } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';

import { FHButton, FHSelect, FProgressBar } from '../components/base';
import { FAutocomplete, FDateTimePicker, FRadioGroup, FSelect } from '../components/baseV2';
import {
  clientActions,
  sessionActions,
  treatmentPlanActions,
} from '../store';
import {
  areUserRolesIn,
  capitalizeFirstLetter,
  disabledFutureDate,
} from '../helpers';
import { dataTableTheme } from '../utils/theme';
import PageContainer from '../components/layout/PageContainer';
import { BackgroundTypes, Roles } from '../constants';
import DataChart from '../components/layout/data-dashboard/DataChart';
import TargetPhaseChangeTable from '../components/layout/data-dashboard/TargetPhaseChangeTable';
import { buildDataDashboardReport } from '../components/layout/data-dashboard/utils';
import {
  getChartLabelByDCM,
  getDataDashboardMeasurementUnitLabel,
  getInitialDashboardOptionSelection,
} from './utils';
import { FORTA_ERROR } from '../utils/colors';
import { linearRegression } from '../helpers/utility-functions';
import { DURATION_PREFIX } from '../components/layout/data-dashboard/constants';
import { TPGoalDataCollectionMethod } from '../components/layout/treatment-plan/goals/common/constants/general';
import {
  DATA_DASHBOARD_DATE_PRESETS,
  DurationChartPlotMethod,
  DurationChartPlotOptions,
} from './constants';
import {
  BT_DASHBOARD_FILTERS_SX,
  CHART_AREA_SX,
  DASHBOARD_FILTERS_SX,
  DASHBOARD_PAGE_SX,
  DATE_FIELDS_SX,
  DETAIL_AREA_SX,
  DURATION_CONTROL_SX,
  FILTER_STEP_AVATAR_SX,
  FILTER_STEP_CONTENT_SX,
  FILTER_STEP_SX,
  GOAL_TARGET_FIELDS_SX,
  LOWER_AREA_SX,
  REPORT_GRID_SX,
  TIME_FILTER_CONTENT_SX,
  TIME_RANGE_RADIO_SX,
} from './styles';

import type {
  IClient,
  IDashboardTarget,
  IDurationTrialAverageTableData,
  IDataDashboardGoalOption,
  IOption,
  ITargetPhaseChange,
  ITrialAverageTableData,
} from '../utils/types';
import type { IDataDashboardTargetOptionState } from '../components/layout/data-dashboard/types';
import type { RootState } from '../store';
import type { Moment } from 'moment';
import type { SyntheticEvent } from 'react';

function DataDashboard() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();

  const { user } = useSelector((state: RootState) => state.auth);
  const { list: clients = [], loading: clientsLoading } = useSelector(
    (state: RootState) => state.clients,
  );
  const {
    trialSummary,
    multiTrialSummaryLoading: sessionLoading,
    dashboardOptions,
    dashboardOptionsLoading,
    dashboardOptionsError,
    dashboardOptionsRequestId,
  } = useSelector((state: RootState) => state.sessions);
  const {
    list: treatmentPlansOfClient,
    activeTreatmentPlan,
    loading: tpLoading,
  } = useSelector((state: RootState) => state.treatmentPlans);

  const isBTView = areUserRolesIn(user?.roles, [Roles.Prbt]);

  const [showNoDataAlert, setShowNoDataAlert] = useState<boolean>(false);
  const [selectedClient, setSelectedClient] = useState<IOption | null>(null);
  const activeTreatmentPlanId =
    isBTView && activeTreatmentPlan?.client_id === selectedClient?.id
      ? activeTreatmentPlan.id
      : null;
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [datePreset, setDatePreset] = useState('last_12_months');
  const [goalTargetsTimeseriesData, setGoalTargetsTimeseriesData] = useState<IDashboardTarget[]>(
    [],
  );
  const [durationChartPlotMethod, setDurationChartPlotMethod] = useState<DurationChartPlotMethod>(
    () => DurationChartPlotMethod.AVERAGE,
  );
  const [filteredPhaseChanges, setfilteredPhaseChanges] = useState<Array<ITargetPhaseChange>>([]);
  const [phaseChangeTableData, setPhaseChangeTableData] = useState<
    Array<{ [key: string]: string }>
  >([]);
  const [trialAverageTableData, setTrialAverageTableData] = useState<ITrialAverageTableData[]>([]);
  const [durationTrialSummaryTableData, setDurationTrialSummaryTableData] = useState<
    IDurationTrialAverageTableData[]
  >([]);
  const [selectedGoal, setSelectedGoal] = useState<IDataDashboardGoalOption | null>(null);
  const [selectedTargets, setSelectedTargets] = useState<IDashboardTarget[]>([]);
  const [targetOptions, setTargetOptions] = useState<IDataDashboardTargetOptionState[]>([]);
  const [showBaseline, setShowBaseline] = useState(true);
  const [chartData, setChartData] = useState<{ [key: string]: string }[]>([]);
  const [tableData, setTableData] = useState<string[][]>([]);
  const [completedRunRows, setCompletedRunRows] = useState<boolean[]>([]);
  const [treatmentPlanTableData, setTreatmentPlanTableData] = useState<
    Array<{ [key: string]: string }>
  >([]);
  const [dcm, setDcm] = useState('');
  const [durationUnit, setDurationUnit] = useState('');
  const [rateUnit, setRateUnit] = useState('');
  const [tableColumnNames, setTableColumnNames] = useState<string[]>([]);
  const [isQueryChanged, setIsQueryChanged] = useState(true);
  const reportRequestId = useRef(0);

  function clearReportData() {
    setGoalTargetsTimeseriesData([]);
    setSelectedTargets([]);
    setfilteredPhaseChanges([]);
    setPhaseChangeTableData([]);
    setTreatmentPlanTableData([]);
    setTrialAverageTableData([]);
    setDurationTrialSummaryTableData([]);
    setTableData([]);
    setTableColumnNames([]);
    setCompletedRunRows([]);
    setChartData([]);
    setDcm('');
    setDurationUnit('');
    setRateUnit('');
    setShowBaseline(false);
  }

  let dateRangeError = '';
  if (startDate && endDate && moment(startDate).isAfter(moment(endDate), 'day')) {
    dateRangeError = 'Start date must be on or before end date.';
  } else if (startDate && endDate && moment(endDate).diff(moment(startDate), 'days') > 364) {
    dateRangeError = 'Date range cannot exceed 365 days.';
  }

  const chartEndDate = useMemo(() => {
    return isBTView && chartData.length
      ? moment
          .unix(+chartData[chartData.length - 1].date)
          .add(5, 'days')
          .format('MM/DD/YY')
      : endDate;
  }, [isBTView, chartData, endDate]);

  const chartStartDate = useMemo(() => {
    return isBTView && chartData.length
      ? moment.unix(+chartData[0].date).subtract(5, 'days').format('MM/DD/YY')
      : startDate;
  }, [isBTView, chartData, startDate]);

  useEffect(() => {
    dispatch(clientActions.getList({ getAccessTokenSilently }));
    dispatch(treatmentPlanActions.getTreatmentPlanStatuses({ getAccessTokenSilently }));
    return () => {
      dispatch(clientActions.clearSelectedClient());
      dispatch(sessionActions.clearDashboardOptions());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (datePreset === 'custom') return;
    const days = isBTView
      ? 365
      : DATA_DASHBOARD_DATE_PRESETS.find((preset) => preset.value === datePreset)?.days ?? 365;
    setStartDate(moment().subtract(days - 1, 'days').format('MM/DD/YY'));
    setEndDate(moment().format('MM/DD/YY'));
  }, [datePreset, isBTView]);

  useEffect(() => {
    reportRequestId.current += 1;
    setIsQueryChanged(true);
    setShowNoDataAlert(false);
    clearReportData();
  }, [startDate, endDate, selectedClient, selectedGoal]);

  useEffect(() => {
    reportRequestId.current += 1;
    setIsQueryChanged(true);
    setShowNoDataAlert(false);
    clearReportData();
  }, [targetOptions]);

  useEffect(() => {
    if (
      isBTView &&
      selectedClient &&
      treatmentPlansOfClient.length > 0 &&
      !activeTreatmentPlan
    ) {
      toast.error("Client doesn't have an active TP");
    }
  }, [activeTreatmentPlan, isBTView, selectedClient, treatmentPlansOfClient]);

  useEffect(() => {
    const selected = goalTargetsTimeseriesData.filter((target) =>
      targetOptions.some((option) => option.checked && option.target_ids.includes(target.id)),
    );
    setSelectedTargets(selected);
    if (targetOptions.filter((t) => t.checked).length > 0 && goalTargetsTimeseriesData.length) {
      const isDataPresent = goalTargetsTimeseriesData.some((goal) => {
        return targetOptions.some(
          (option) => option.checked && option.target_ids.includes(goal.id),
        );
      });
      if (!isDataPresent) {
        setShowNoDataAlert(true);
      }
    }
  }, [goalTargetsTimeseriesData, targetOptions]);

  useEffect(() => {
    setSelectedGoal(null);
    setTargetOptions([]);
    dispatch(sessionActions.clearDashboardOptions());
    if (
      !selectedClient?.id ||
      !startDate ||
      !endDate ||
      dateRangeError ||
      (isBTView && !activeTreatmentPlanId)
    ) {
      return;
    }
    const params = {
      client_id: selectedClient.id,
      start_date: moment(startDate).format('YYYY-MM-DD'),
      end_date: moment(endDate).format('YYYY-MM-DD'),
    };
    if (activeTreatmentPlanId) {
      params.treatment_plan_id = activeTreatmentPlanId;
    }
    dispatch(
      sessionActions.getDashboardOptions({
        params,
        getAccessTokenSilently,
      }),
    );
  }, [
    activeTreatmentPlanId,
    dateRangeError,
    dispatch,
    endDate,
    getAccessTokenSilently,
    isBTView,
    selectedClient?.id,
    startDate,
  ]);

  useEffect(() => {
    const { goalId } = getInitialDashboardOptionSelection(dashboardOptions);
    setSelectedGoal(dashboardOptions.find((goal) => goal.id === goalId) ?? null);
  }, [dashboardOptions]);

  // clear targets when client is changed
  useEffect(() => {
    if (!selectedClient || !selectedClient?.id) {
      dispatch(clientActions.clearSelectedClient());
      dispatch(treatmentPlanActions.clearTreatmentPlan());
      return;
    }
    dispatch(treatmentPlanActions.clearTreatmentPlan());
    dispatch(
      treatmentPlanActions.getTreatmentPlanByQuery({
        query: { clientId: selectedClient.id },
        getAccessTokenSilently,
      }),
    );
    setSelectedGoal(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient]);

  // set target options on goal select
  useEffect(() => {
    const options: IDataDashboardTargetOptionState[] = [];
    selectedGoal?.targets.forEach((target) => {
      options.push({
        ...target,
        checked: false,
      });
    });
    if (options.length === 1) {
      options[0].checked = true;
    }
    setTargetOptions(options);
  }, [selectedGoal]);


  useEffect(() => {
    if (!trialSummary || !selectedGoal || !startDate || !endDate) return;
    const checkedTargets = targetOptions.filter((target) => target.checked);
    if (trialSummary.length === 0) {
      clearReportData();
      setShowNoDataAlert(checkedTargets.length > 0);
      return;
    }
    const report = buildDataDashboardReport({
      selectedTargets: checkedTargets,
      goalSummaries: trialSummary,
      startDate: moment(startDate).format('YYYY-MM-DD'),
      endDate: moment(endDate).format('YYYY-MM-DD'),
    });
    const reportTargets = report.targets.map((target) => ({
      id: target.sourceTargetIds[0],
      label: target.label,
      type: target.sessions[0]?.data_collection_method ?? '',
      event_duration_measurement_unit:
        trialSummary
          .flatMap((summary) => summary.targets)
          .find((sourceTarget) => target.sourceTargetIds.includes(sourceTarget.target_id))
          ?.event_duration_measurement_unit ?? '',
      timeSeriesData: target.sessions.map((session) => ({
        ...session,
        target_label: target.label,
        target_id: target.sourceTargetIds[0],
        type: session.data_collection_method ?? '',
      })),
    }));
    const firstSession = report.targets.flatMap((target) => target.sessions)[0];
    const reportDcm = firstSession?.data_collection_method ?? '';
    const reportDurationUnit =
      reportDcm === TPGoalDataCollectionMethod.INSTANCES
        ? reportTargets.find((target) => target.event_duration_measurement_unit)
            ?.event_duration_measurement_unit ?? ''
        : firstSession?.measurement_units_duration_unit ?? '';
    setDcm(reportDcm);
    setDurationUnit(reportDurationUnit);
    setRateUnit(firstSession?.measurement_units_interval_unit ?? '');
    setGoalTargetsTimeseriesData(reportTargets);
    setSelectedTargets(reportTargets);
    setShowNoDataAlert(checkedTargets.length > 0 && report.detailRows.length === 0);

    const phases = report.phaseRows.map((phase) => ({
      id: `${phase.sourceTargetId}:${phase.timestamp}`,
      date_added: phase.timestamp,
      date_timestamp: moment(phase.timestamp).unix(),
      target_id: phase.sourceTargetId,
      title: phase.title,
      line_label: moment(phase.timestamp).format('MM/DD'),
      type: phase.type,
    }));
    setfilteredPhaseChanges(phases);
    setPhaseChangeTableData(
      report.phaseRows.map((phase) => ({
        date: phase.date,
        title: phase.title,
        target: phase.target,
      })),
    );
    setTreatmentPlanTableData(
      report.treatmentPlanRows.map(({ treatmentPlan, start, end, status }) => ({
        treatmentPlan,
        start,
        end,
        status,
      })),
    );
    setTrialAverageTableData(
      report.targetSummaries.map((summary) => ({
        target: summary.target,
        label:
          reportDcm === TPGoalDataCollectionMethod.PERCENTAGE
            ? 'Average success percentage'
            : reportDcm === TPGoalDataCollectionMethod.RATE
              ? 'Average rate'
              : reportDcm === TPGoalDataCollectionMethod.DURATION
                ? 'Average duration'
                : summary.hasInstanceDurations
                  ? 'Average duration of instances'
                  : 'Average of Instances',
        value: String(summary.value),
        unit:
          reportDcm === TPGoalDataCollectionMethod.PERCENTAGE
            ? '%'
              : reportDcm === TPGoalDataCollectionMethod.INSTANCES && !summary.hasInstanceDurations
              ? 'instances'
              : summary.hasInstanceDurations
                ? reportTargets
                    .find((target) => target.label === summary.target)
                    ?.event_duration_measurement_unit?.toLowerCase() ?? ''
                : '',
      })),
    );
    setDurationTrialSummaryTableData(
      report.targetSummaries.map((summary) => ({
        target: summary.target,
        label: 'Duration summary',
        average: String(summary.average),
        total: String(summary.total),
      })),
    );

    if (report.targets.length === 1) {
      setTableData(
        report.detailRows.map((row) => {
          const value =
            reportDcm === TPGoalDataCollectionMethod.PERCENTAGE
              ? `${row.success}/${row.attempts} (${row.percentage}%)`
              : reportDcm === TPGoalDataCollectionMethod.RATE
                ? String(row.rate)
                : reportDcm === TPGoalDataCollectionMethod.DURATION
                  ? String(
                      durationChartPlotMethod === DurationChartPlotMethod.AVERAGE
                        ? row.durationAverage
                        : row.durationTotal,
                    )
                  : String(row.instances);
          const result =
            row.threshold_status === 'met'
              ? 'Met'
              : row.threshold_status === 'not_met'
                ? 'Not met'
                : 'Not applicable';
          return [
            row.date,
            row.time,
            row.target,
            row.treatmentPlan,
            value,
            `${row.threshold ?? '—'} (${row.required_count ?? '—'}) - ${result}`,
          ];
        }),
      );
      setTableColumnNames([
        'Date',
        'Time',
        'Target',
        'Treatment plan',
        'Measurement',
        'Mastering/over',
      ]);
      setCompletedRunRows(report.detailRows.map((row) => Boolean(row.in_completed_run)));
    } else {
      setTableData([]);
      setTableColumnNames([]);
      setCompletedRunRows([]);
    }

    const chartRowsBySession = new Map<string, { [key: string]: string }>();
    report.targets.forEach((target) => {
      target.sessions.forEach((session) => {
        const row = chartRowsBySession.get(session.session_key) ?? {
          date: String(moment(session.timestamp).unix()),
        };
        const value =
          session.data_collection_method === TPGoalDataCollectionMethod.PERCENTAGE
            ? session.percentage
            : session.data_collection_method === TPGoalDataCollectionMethod.RATE
              ? session.rate
              : session.data_collection_method === TPGoalDataCollectionMethod.DURATION
                ? durationChartPlotMethod === DurationChartPlotMethod.AVERAGE
                  ? session.durationEventAverage
                  : session.durationEventTotal
                : session.instances;
        row[target.label] = String(value);
        row[`${target.label}__status`] = session.threshold_status ?? 'not_applicable';
        if (session.threshold !== null && session.threshold !== undefined) {
          row[`${target.label}__threshold`] = String(session.threshold);
        }
        if (session.durations?.length) {
          row[`${DURATION_PREFIX}${target.label}`] = `${session.durations.join(', ')} -- (${
            session.total_duration ?? 0
          })`;
        }
        chartRowsBySession.set(session.session_key, row);
      });
      const points = target.sessions
        .map((session) => ({
          key: session.session_key,
          x: moment(session.timestamp).unix(),
          y: Number(chartRowsBySession.get(session.session_key)?.[target.label]),
        }))
        .filter((point) => Number.isFinite(point.y))
        .sort((a, b) => a.x - b.x);
      if (points.length > 1) {
        const { slope, intercept } = linearRegression(points);
        const firstPoint = chartRowsBySession.get(points[0].key);
        const lastPoint = chartRowsBySession.get(points[points.length - 1].key);
        if (firstPoint) firstPoint[`${target.label}_trend`] = String(slope * points[0].x + intercept);
        if (lastPoint) {
          lastPoint[`${target.label}_trend`] = String(
            slope * points[points.length - 1].x + intercept,
          );
        }
      }
    });
    const sessionChartRows = [...chartRowsBySession.values()].sort(
      (a, b) => Number(a.date) - Number(b.date),
    );
    report.targets.forEach((target) => {
      const sessions = [...target.sessions].sort((a, b) =>
        a.timestamp.localeCompare(b.timestamp),
      );
      let currentSessionIndex = -1;
      sessionChartRows.forEach((row) => {
        while (
          currentSessionIndex + 1 < sessions.length &&
          moment(sessions[currentSessionIndex + 1].timestamp).unix() <= Number(row.date)
        ) {
          currentSessionIndex += 1;
        }
        if (
          currentSessionIndex < 0 ||
          Number(row.date) > moment(sessions[sessions.length - 1].timestamp).unix()
        ) {
          return;
        }
        const threshold = sessions[currentSessionIndex].threshold;
        if (threshold === null || threshold === undefined) {
          delete row[`${target.label}__threshold`];
        } else {
          row[`${target.label}__threshold`] = String(threshold);
        }
      });
    });
    report.baselines.forEach((baseline) => {
      if (baseline.value === null) return;
      chartRowsBySession.set(`baseline:${baseline.goalId}`, {
        date: String(moment(baseline.date).unix()),
        [`Baseline ${baseline.treatmentPlan}`]: String(baseline.value),
      });
    });
    const maxYAxisValue = Math.max(
      0,
      ...sessionChartRows.flatMap((row) =>
        report.targets.map((target) => Number(row[target.label] ?? 0)),
      ),
    );
    report.phaseRows.forEach((phase) => {
      const timestamp = moment(phase.timestamp).unix();
      chartRowsBySession.set(`phase:${phase.sourceTargetId}:${phase.timestamp}:start`, {
        date: String(timestamp),
        [phase.title]: '0',
      });
      chartRowsBySession.set(`phase:${phase.sourceTargetId}:${phase.timestamp}:end`, {
        date: String(timestamp + 10),
        [phase.title]: String(maxYAxisValue + 5),
      });
    });
    setChartData(
      [...chartRowsBySession.values()].sort((a, b) => Number(a.date) - Number(b.date)),
    );
    setShowBaseline(report.baselines.some((baseline) => baseline.value !== null));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Apply controls only after a new report response replaces trialSummary.
  }, [trialSummary, durationChartPlotMethod]);


  function requestReport() {
    if (!selectedClient?.id || !selectedGoal || !startDate || !endDate) return;
    const requestId = reportRequestId.current + 1;
    reportRequestId.current = requestId;
    const params = {
      client_id: selectedClient.id,
      start_date: moment(startDate).format('YYYY-MM-DD'),
      end_date: moment(endDate).format('YYYY-MM-DD'),
      goal_ids: selectedGoal.goal_ids,
      duration_mode: durationChartPlotMethod.toLowerCase(),
    };
    if (activeTreatmentPlanId) params.treatment_plan_id = activeTreatmentPlanId;
    dispatch(
      sessionActions.getMultiTrialSummary({
        params,
        getAccessTokenSilently,
      }),
    ).then((action) => {
      if (requestId !== reportRequestId.current) return;
      if (!Array.isArray(action.payload)) {
        clearReportData();
        return;
      }
      setIsQueryChanged(false);
      dispatch(
        sessionActions.updateState({
          key: 'trialSummary',
          data: action.payload.flat(),
        }),
      );
    });
  }

  function renderSelectedTargets(values: IDataDashboardTargetOptionState[]) {
    return <Typography noWrap>{values.map((target) => target.label).join(', ')}</Typography>;
  }

  function handleDashboardOptionsRetry() {
    if (
      !selectedClient?.id ||
      !startDate ||
      !endDate ||
      dateRangeError ||
      (isBTView && !activeTreatmentPlanId)
    ) {
      return;
    }
    const params = {
      client_id: selectedClient.id,
      start_date: moment(startDate).format('YYYY-MM-DD'),
      end_date: moment(endDate).format('YYYY-MM-DD'),
    };
    if (activeTreatmentPlanId) {
      params.treatment_plan_id = activeTreatmentPlanId;
    }
    dispatch(
      sessionActions.getDashboardOptions({
        params,
        getAccessTokenSilently,
      }),
    );
  }

  return (
    <ThemeProvider theme={dataTableTheme}>
      <PageContainer
        dataTestId="data-dashboard-container"
        sx={DASHBOARD_PAGE_SX}
      >
        {(clientsLoading || tpLoading || sessionLoading || dashboardOptionsLoading) && (
          <FProgressBar />
        )}
        <Box sx={isBTView ? BT_DASHBOARD_FILTERS_SX : DASHBOARD_FILTERS_SX}>
          <Box sx={FILTER_STEP_SX}>
            <Avatar sx={FILTER_STEP_AVATAR_SX}>1</Avatar>
            <Box sx={FILTER_STEP_CONTENT_SX}>
              <FAutocomplete<IOption>
                data-testid="data-dashboard-client"
                label="Client name"
                required
                fullWidth
                value={selectedClient}
                options={clients.map((client: IClient) => ({
                  id: client.id,
                  label: `${client.first_name} ${client.last_name}`,
                }))}
                placeholder="Select client"
                disableClearable
                onChange={(_event: SyntheticEvent, newValue: IOption | null) => {
                  setSelectedClient(newValue);
                }}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option: IOption, value: IOption) => option.id === value.id}
              />
            </Box>
          </Box>
          {!isBTView && user && (
            <Box sx={FILTER_STEP_SX}>
              <Avatar sx={FILTER_STEP_AVATAR_SX}>2</Avatar>
              <Box sx={TIME_FILTER_CONTENT_SX}>
                <FRadioGroup
                  data-testid="data-dashboard-date-preset"
                  name="data-dashboard-date-preset"
                  label="Time frame"
                  row
                  fullWidth
                  value={datePreset}
                  options={DATA_DASHBOARD_DATE_PRESETS.map(({ value, label }) => ({
                    value,
                    label,
                  })).concat({ value: 'custom', label: 'Custom' })}
                  sx={TIME_RANGE_RADIO_SX}
                  onChange={(event) => setDatePreset(event.target.value)}
                />
                <Box sx={DATE_FIELDS_SX}>
                  <FDateTimePicker
                    data-testid="startDate"
                    id="data-dashboard-start-date"
                    label="Start date"
                    required
                    dateOnly
                    fullWidth
                    format="MM/DD/YYYY"
                    placeholder="Select date"
                    allowClear={false}
                    value={startDate ? moment(startDate, 'MM/DD/YY') : null}
                    disabledDate={disabledFutureDate}
                    onChange={(value: Moment | null) => {
                      if (!value) return;
                      setDatePreset('custom');
                      setStartDate(value.format('MM/DD/YY'));
                    }}
                  />
                  <FDateTimePicker
                    data-testid="dateEnd"
                    id="data-dashboard-end-date"
                    label="End date"
                    required
                    dateOnly
                    fullWidth
                    format="MM/DD/YYYY"
                    placeholder="Select date"
                    allowClear={false}
                    value={endDate ? moment(endDate, 'MM/DD/YY') : null}
                    disabledDate={disabledFutureDate}
                    onChange={(value: Moment | null) => {
                      if (!value) return;
                      setDatePreset('custom');
                      setEndDate(value.format('MM/DD/YY'));
                    }}
                  />
                </Box>
              </Box>
            </Box>
          )}
          <Box sx={FILTER_STEP_SX}>
            <Avatar sx={FILTER_STEP_AVATAR_SX}>{isBTView ? 2 : 3}</Avatar>
            <Box sx={GOAL_TARGET_FIELDS_SX}>
              <FSelect
                data-testid="data-dashboard-goal"
                label="Goal"
                required
                fullWidth
                displayEmpty
                disabled={dashboardOptionsLoading || dashboardOptions.length === 0}
                options={dashboardOptions
                  .map((goalOfClient: IDataDashboardGoalOption) => ({
                    value: goalOfClient.id,
                    label: `${capitalizeFirstLetter(goalOfClient.label)} (${getChartLabelByDCM(
                      goalOfClient.data_collection_method,
                    )}${getDataDashboardMeasurementUnitLabel(goalOfClient)})`,
                  }))
                  .sort((a, b) => a.label.localeCompare(b.label))}
                value={selectedGoal ? selectedGoal?.id : ''}
                onUpdate={(value) =>
                  setSelectedGoal(dashboardOptions.find((goal) => goal.id === value) || null)
                }
                renderValue={(value) => {
                  if (!value) return 'Select goal';
                  const selectedGoalItem = dashboardOptions.find((goal) => goal.id === value);
                  return `${capitalizeFirstLetter(selectedGoalItem?.label || '')}${
                    selectedGoalItem
                      ? ` (${getChartLabelByDCM(
                          selectedGoalItem.data_collection_method,
                        )}${getDataDashboardMeasurementUnitLabel(selectedGoalItem)})`
                      : ''
                  }`;
                }}
              />
              <FAutocomplete<IDataDashboardTargetOptionState, true, false, false>
                data-testid="data-dashboard-targets"
                label="Target"
                required
                fullWidth
                multiple
                disableCloseOnSelect
                placeholder="Select target"
                value={targetOptions.filter((target) => target.checked)}
                options={targetOptions}
                disabled={targetOptions?.length === 0}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderTags={renderSelectedTargets}
                onChange={(_event: SyntheticEvent, values) => {
                  setTargetOptions(
                    targetOptions.map((target: IDataDashboardTargetOptionState) => {
                      return {
                        ...target,
                        checked: values.some((value) => value.id === target.id),
                      };
                    }),
                  );
                }}
              />
              <FHButton
                data-testid="data-dashboard-generate-chart"
                disabled={
                  !selectedClient?.id ||
                  !selectedGoal ||
                  !startDate ||
                  !endDate ||
                  Boolean(dateRangeError) ||
                  !targetOptions.some((target) => target.checked) ||
                  !isQueryChanged
                }
                onClick={requestReport}
              >
                Generate Chart
              </FHButton>
            </Box>
          </Box>
          {dcm === TPGoalDataCollectionMethod.DURATION && selectedTargets.length > 0 && (
            <Box sx={DURATION_CONTROL_SX}>
              <Typography fontWeight="bold" align="left" width={'200px'}>
                Display chart by:
              </Typography>
              <FHSelect
                sx={{ width: '100%' }}
                inputHeight={40}
                backgroundType={BackgroundTypes.Colored}
                disabled={chartData?.length === 0}
                options={DurationChartPlotOptions}
                optionDataTestIdPrefix="baseLineLevelOption"
                value={durationChartPlotMethod}
                onUpdate={(value) => {
                  if (
                    value === DurationChartPlotMethod.AVERAGE ||
                    value === DurationChartPlotMethod.TOTAL
                  ) {
                    setDurationChartPlotMethod(value);
                  }
                }}
              />
            </Box>
          )}
        </Box>
        {dateRangeError && (
          <Typography color={FORTA_ERROR} mb={3}>
            {dateRangeError}
          </Typography>
        )}
        {selectedClient &&
          dashboardOptionsRequestId &&
          !dashboardOptionsLoading &&
          !dashboardOptionsError &&
          dashboardOptions.length === 0 && (
          <Typography color={FORTA_ERROR} mb={3}>
            No goals contain data for the selected date range.
          </Typography>
        )}
        {dashboardOptionsError && (
          <Box mb={3}>
            <Typography color={FORTA_ERROR} mb={1}>
              Unable to load goals and targets. Try again.
            </Typography>
            <FHButton onClick={handleDashboardOptionsRetry}>
              Retry
            </FHButton>
          </Box>
        )}
        {showNoDataAlert && (
          <Typography color={FORTA_ERROR}>No data present for selected Target(s).</Typography>
        )}
        <Box sx={REPORT_GRID_SX}>
          {selectedTargets && selectedTargets.length > 0 && (
            <Box data-testid="chartArea" sx={CHART_AREA_SX}>
              <DataChart
                endDate={chartEndDate}
                durationUnit={durationUnit.toLowerCase()}
                rateUnit={rateUnit.toLowerCase()}
                durationChartPlotMethod={durationChartPlotMethod}
                startDate={chartStartDate}
                filteredPhaseChanges={filteredPhaseChanges}
                selectedTargets={selectedTargets}
                showBaselineDataPoint={
                  trialSummary?.length > 0 &&
                  Boolean(selectedGoal?.goal_ids.includes(trialSummary[0]?.goal_id)) &&
                  showBaseline
                }
                chartData={chartData}
              />
            </Box>
          )}
          <Box sx={DETAIL_AREA_SX}>
            {selectedTargets.length === 1 && (
              <TargetPhaseChangeTable
                dcm={dcm}
                durationUnit={durationUnit.toLowerCase()}
                rateUnit={rateUnit.toLowerCase()}
                tableData={tableData}
                tableColumnNames={tableColumnNames}
                phaseChangeTableData={phaseChangeTableData}
                trialAverageTableData={trialAverageTableData}
                durationTrialAverageTableData={durationTrialSummaryTableData}
                selectedTargetsCount={selectedTargets.length}
                showLowerPanels={false}
                completedRunRows={completedRunRows}
              />
            )}
          </Box>
          {selectedTargets.length > 0 && (
            <Box sx={LOWER_AREA_SX}>
              <TargetPhaseChangeTable
                dcm={dcm}
                durationUnit={durationUnit.toLowerCase()}
                rateUnit={rateUnit.toLowerCase()}
                tableData={[]}
                tableColumnNames={[]}
                phaseChangeTableData={phaseChangeTableData}
                trialAverageTableData={trialAverageTableData}
                durationTrialAverageTableData={durationTrialSummaryTableData}
                selectedTargetsCount={selectedTargets.length}
                showDetailTable={false}
                treatmentPlanTableData={treatmentPlanTableData}
              />
            </Box>
          )}
        </Box>
      </PageContainer>
    </ThemeProvider>
  );
}

export default DataDashboard;
